import { useState, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { acceptInviteRequestSchema, STAFF_PASSWORD_MIN } from '@manaro/contracts'
import { useAcceptInvite, useInvite } from '@/features/staff/use-staff.ts'
import { ApiError } from '@/shared/api/client.ts'
import { homePath } from '@/shared/config/navigation.ts'
import { Button } from '@/shared/ui/button.tsx'
import { Field } from '@/shared/ui/field.tsx'
import { Logo } from '@/shared/ui/logo.tsx'
import { Spinner } from '@/shared/ui/spinner.tsx'
import { Alert } from '@/shared/ui/states.tsx'

function SetPasswordForm({ token, email, name }: { token: string; email: string; name: string }) {
  const navigate = useNavigate()
  const accept = useAcceptInvite(token)
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (accept.isPending) return
    const parsed = acceptInviteRequestSchema.safeParse({ token, password })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Проверьте пароль.')
      return
    }
    if (password !== repeat) {
      setError('Пароли не совпадают.')
      return
    }
    setError(null)
    accept.mutate(parsed.data.password, {
      onSuccess: (session) => navigate(homePath(session.user.sections), { replace: true }),
      onError: (cause) => setError(cause instanceof ApiError ? cause.message : 'Не получилось — попробуйте ещё раз.'),
    })
  }

  const clearError = () => setError(null)

  return (
    <form onSubmit={submit} className="flex flex-col gap-4.5" noValidate>
      <div>
        <h1 className="mb-1.5 font-display text-2xl font-semibold tracking-[-0.015em]">{name}, добро пожаловать</h1>
        <p className="text-[14px] text-ink-muted text-balance-pretty">
          Задайте пароль для входа в бэк-офис Manaro. Почта для входа — <span className="text-ink">{email}</span>.
        </p>
      </div>

      {error && <Alert>{error}</Alert>}

      <input type="email" autoComplete="username" value={email} readOnly hidden />
      <Field
        label="Пароль"
        type={showPassword ? 'text' : 'password'}
        autoComplete="new-password"
        placeholder={`Не короче ${STAFF_PASSWORD_MIN} символов`}
        value={password}
        autoFocus
        onChange={(event) => {
          setPassword(event.target.value)
          clearError()
        }}
        action={
          <button
            type="button"
            onClick={() => setShowPassword((value) => !value)}
            className="text-[12.5px] text-brand hover:text-brand-dark"
          >
            {showPassword ? 'Скрыть' : 'Показать'}
          </button>
        }
      />
      <Field
        label="Ещё раз"
        type={showPassword ? 'text' : 'password'}
        autoComplete="new-password"
        value={repeat}
        onChange={(event) => {
          setRepeat(event.target.value)
          clearError()
        }}
      />

      <Button type="submit" loading={accept.isPending}>
        {accept.isPending ? 'Сохраняем…' : 'Сохранить пароль и войти'}
      </Button>
    </form>
  )
}

export function InvitePage() {
  // The token sits after «#», so it never reaches the server's logs.
  const token = useLocation().hash.slice(1)
  const invite = useInvite(token)

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5.5 bg-canvas px-5 pt-8 pb-10">
      <div className="flex flex-col items-center gap-1.5">
        <Logo height={26} />
        <div className="text-[12px] text-ink-muted">админка сервиса</div>
      </div>

      <div className="w-full max-w-[416px] rounded-panel border border-line bg-surface px-7.5 pt-7.5 pb-6.5 shadow-panel">
        {invite.isPending ? (
          <div className="flex items-center justify-center gap-2 py-6 text-[13.5px] text-ink-muted">
            <Spinner className="size-3.5 text-brand" />
            Проверяем ссылку…
          </div>
        ) : invite.isError ? (
          <div className="flex flex-col gap-3">
            <h1 className="font-display text-2xl font-semibold tracking-[-0.015em]">Ссылка не работает</h1>
            <Alert>
              {invite.error instanceof ApiError ? invite.error.message : 'Не удалось проверить ссылку — обновите страницу.'}
            </Alert>
          </div>
        ) : (
          <SetPasswordForm token={token} email={invite.data.email} name={invite.data.name} />
        )}
      </div>
    </div>
  )
}
